/*
 * The SongShake FX engine (ENG-804, owned in-house since ENG-871):
 * `src/fx/engine/songshake-fx-core.js`, its stylesheet and `defaults.json`.
 * The app ships exactly one copy; every package (vibe set, boost animation,
 * effect rules) runs on it. There is no engine version and no handshake with
 * the backend: packages are checked by shape (fxPackages.js), and the backend's
 * copies of the defaults and the crowd model are kept in step by
 * `npm run fx:sync-defaults` and the tests in fxEngine.test.js.
 */
import SongShakeFXEngine from '../fx/engine/songshake-fx-core.js';
import '../fx/engine/songshake-fx.css';

const isEngine = (x) => Boolean(x && typeof x.createStage === 'function' && typeof x.registerPackage === 'function');

/** The engine's API (`createStage`, `registerPackage`, `getPackage`, `flip`, `energy`, …), or null. */
export const SongShakeFX = isEngine(SongShakeFXEngine) ? SongShakeFXEngine : null;

/**
 * Whether this browser can run the engine: Canvas 2D, `Path2D`, `DOMParser`,
 * `ResizeObserver` and `requestAnimationFrame` (the engine's README). jsdom
 * has none of the canvas ones, so tests never start a real stage unless they
 * provide them.
 */
export const fxSupported = () => {
  if (!SongShakeFX || typeof window === 'undefined' || typeof document === 'undefined') return false;
  return typeof window.Path2D === 'function'
    && typeof window.CanvasRenderingContext2D === 'function'
    && typeof window.DOMParser === 'function'
    && typeof window.ResizeObserver === 'function'
    && typeof window.requestAnimationFrame === 'function';
};

/**
 * Whether the QA hooks are on: a dev build, `?fxdebug` in the URL, or
 * `localStorage['songshake:fx-debug'] === '1'`. With them on the stream page
 * exposes its stage as `window.songshakeFx`, so QA can set
 * `songshakeFx.timeScale` (speeds up the afterglow cool-down) and
 * `songshakeFx.debugBounds` (draws each card's allowed area and the stream's
 * edge). `?fxdebug=bounds` turns the bounds on from the start.
 */
export const fxDebug = () => {
  let on = false;
  let bounds = false;
  try {
    if (import.meta.env?.DEV && import.meta.env?.MODE !== 'test') on = true;
  } catch (_e) {
    // No import.meta.env outside Vite.
  }
  try {
    const param = new URLSearchParams(window.location.search).get('fxdebug');
    if (param != null) {
      on = true;
      bounds = param === 'bounds';
    }
  } catch (_e) {
    // No location.
  }
  try {
    if (window.localStorage.getItem('songshake:fx-debug') === '1') on = true;
  } catch (_e) {
    // Storage may be blocked.
  }
  return { on, bounds };
};
